import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Instagram, Phone } from "lucide-react";
// CategoryNav removed
// InstagramWidget removed — only on home page
import CategoryHero from "./CategoryHero";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';

// Every organizer/production company listing's public detail-page URL is
// derived from its name: /event-production/<name-slug>. Same pattern as
// templeSlug in Temples.tsx and dessertSlug in Desserts.tsx.
export function organizerSlug(name: string) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

const ORGANIZER_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "city", label: "City", placeholder: "Dallas–Fort Worth" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "phone", label: "Phone" },
  { key: "website", label: "Website URL", type: "url", placeholder: "https://…" },
  { key: "contactUrl", label: "Contact URL (Instagram/Facebook)", type: "url" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  { key: "description", label: "Description", type: "textarea" },
  ...SOCIAL_FIELDS,
];

export interface EventOrganizerItem {
  name: string;
  city: string;
  address: string;
  website: string;
  phone: string;
  image: string;
  mapsLink?: string;
  contactUrl?: string;
  description?: string;
}

export const EVENT_ORGANIZERS: EventOrganizerItem[] = [
  {
    name: "Gargi Productions",
    city: "Dallas–Fort Worth",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783439431224-199bdg.png",
    contactUrl: "https://www.instagram.com/gargiproductions/",
  },
  {
    name: "3SixtyShows",
    city: "Dallas–Fort Worth",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784535547954-5hj2xt.jpg",
    contactUrl: "https://www.instagram.com/3sixtyshows/",
  },
  {
    name: "Flashbrush Production",
    city: "Dallas–Fort Worth",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784535547975-er12x.jpg",
    contactUrl: "https://www.instagram.com/flashbrushproduction/",
  },
  {
    name: "EnStories Production",
    city: "Dallas–Fort Worth",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784535547973-v4jfbe.jpg",
    contactUrl: "https://www.instagram.com/enstoriesproduction/",
  },
  {
    name: "FocusEnt Dallas",
    city: "Dallas–Fort Worth",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=600&h=400&fit=crop",
    contactUrl: "https://www.instagram.com/focusentdallas/",
  },
  {
    name: "udc_usa",
    city: "Dallas–Fort Worth",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=600&h=400&fit=crop",
    contactUrl: "https://www.instagram.com/udeusa/",
  },
  {
    name: "Maha Buzz",
    city: "Dallas–Fort Worth",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783476654919-wdlc5a.png",
    contactUrl: "https://www.instagram.com/suthradhaar/",
  },
  {
    name: "Sona Events For You",
    city: "Dallas–Fort Worth",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784285866800-l7jjtp.png",
    contactUrl: "https://www.instagram.com/sonaeventsforyou/",
  },
  {
    // Moved here from the Event Management (Decorators) page per admin request.
    name: "AJ Entertainments",
    city: "Dallas, TX",
    address: "Dallas, TX",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784306704823-otv6on.jpg",
    contactUrl: "https://www.instagram.com/aj_events_promotions/",
  },
];

// "AJ Events & Decors" moved to the Event Management page (Decorators,
// route /event-management) per admin request — keep it out of this page's
// list even if a stale admin-added row for it still exists in this page's
// data store.
const MOVED_OUT_ORGANIZER_NAMES = new Set(["aj events & decors"]);

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=600&h=400&fit=crop";

// Names for which the location/address line should always be hidden,
// regardless of what address value is stored (covers entries added via Admin).
const HIDE_LOCATION_FOR = new Set([
  "aj entertainments",
  "lucky charms decor",
  "lucky charms decor, wedding decor by prathyusha",
  "wedding decor by prathyusha",
  "tastebuds by anubhi",
]);

// This card should always render first on the page, regardless of when it
// was added to the managed collection.
const PIN_TO_FRONT = new Set(["tastebuds by anubhi"]);

// Default description text for specific cards, applied whenever the card's own
// `description` field is empty. Keyed by lowercase name so this works whether
// the card is a code default or an admin-added (custom) item.
const FORCE_DESCRIPTION_BY_NAME: Record<string, string> = {
  "tastebuds by anubhi": "Brands, Marketing and Collaborations",
};

function effectiveDescription(name: string, description?: string): string | undefined {
  if (description && description.trim() !== "") return description;
  return FORCE_DESCRIPTION_BY_NAME[(name || "").toLowerCase()];
}

// These cards had their image freshly updated in code, but a stale admin
// override row (from before) can still hold the old image and win the
// merge inside useManagedCollection (overrides > defaults). Force these
// specific cards to always use the current code default image so the new
// photo actually shows, regardless of any stored override.
const FORCE_DEFAULT_IMAGE = new Set([
  "3sixtyshows",
  "flashbrush production",
  "enstories production",
]);
const DEFAULT_IMAGE_BY_NAME: Record<string, string> = Object.fromEntries(
  EVENT_ORGANIZERS.map((o) => [o.name.toLowerCase(), o.image])
);

function shouldHideLocation(name: string) {
  const n = (name || "").toLowerCase();
  for (const key of HIDE_LOCATION_FOR) {
    if (n.includes(key)) return true;
  }
  return false;
}

export function OrganizerCard({ organizer, index }: { organizer: EventOrganizerItem; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <Link to={`/event-production/${organizerSlug(organizer.name)}`} className="flex flex-col flex-1">
        <div className="w-full aspect-[4/3] relative overflow-hidden">
          <img
            src={
              FORCE_DEFAULT_IMAGE.has((organizer.name || "").toLowerCase())
                ? DEFAULT_IMAGE_BY_NAME[(organizer.name || "").toLowerCase()] || organizer.image
                : organizer.image
            }
            alt={organizer.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
            }}
          />
        </div>
        <div className="p-3 pb-1.5 flex-1">
          <h3 className="text-[14px] text-gray-900 leading-tight mb-1 line-clamp-2">
            {(() => {
              const n = organizer.name || "";
              const commaIdx = n.indexOf(",");
              if (n.toLowerCase().includes("lucky charms decor") && commaIdx !== -1) {
                return (
                  <>
                    <span className="font-bold">{n.slice(0, commaIdx)}</span>
                    <span className="font-normal">{n.slice(commaIdx)}</span>
                  </>
                );
              }
              return <span className="font-bold">{n}</span>;
            })()}
          </h3>
          {organizer.address &&
            organizer.address.trim() !== "" &&
            organizer.address !== "Address coming soon" &&
            !shouldHideLocation(organizer.name) && (
            <div className="flex items-start gap-1">
              <MapPin className="w-3 h-3 text-orange-500 shrink-0 mt-0.5" />
              <span className="text-[11px] text-gray-500 font-medium leading-snug line-clamp-2">
                {organizer.address}
              </span>
            </div>
          )}
          {effectiveDescription(organizer.name, organizer.description) && (
            <p className="text-[11px] text-gray-500 font-normal leading-snug line-clamp-2 mt-0.5">
              {effectiveDescription(organizer.name, organizer.description)}
            </p>
          )}
        </div>
      </Link>
      <div className="px-3 pb-3 pt-1">
        <div className="mb-2">
          <SocialLinks item={organizer} px={20} />
        </div>
        {(() => {
          // A real phone number (not the "Coming soon" placeholder) gets a
          // Call button whenever there is no social/contact URL on the card.
          const rawPhone = (organizer.phone || "").trim();
          const isRealPhone =
            rawPhone !== "" &&
            rawPhone.toLowerCase() !== "coming soon" &&
            /\d/.test(rawPhone);
          if (organizer.contactUrl) {
            return (
              <a
                href={organizer.contactUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-1.5 bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] hover:opacity-90 text-white text-[12px] font-semibold py-2 rounded-md transition-opacity duration-200"
              >
                <Instagram className="w-3.5 h-3.5" /> Instagram
              </a>
            );
          }
          if (isRealPhone) {
            return (
              <a
                href={`tel:${rawPhone.replace(/[^\d+]/g, "")}`}
                className="w-full flex items-center justify-center gap-1.5 bg-[#51ba4f] hover:opacity-90 text-white text-[12px] font-semibold py-2 rounded-md transition-opacity duration-200"
              >
                <Phone className="w-3.5 h-3.5" /> Call
              </a>
            );
          }
          return (
            <span className="w-full flex items-center justify-center gap-1.5 bg-gray-50 border border-gray-200 text-gray-400 text-[12px] font-semibold py-2 rounded-md cursor-not-allowed">
              <Instagram className="w-3.5 h-3.5" /> Instagram
            </span>
          );
        })()}
      </div>
    </motion.div>
  );
}

export default function EventOrganizer() {
  const [query, setQuery] = useState("");
  const mc = useManagedCollection<any>("event_organizers", EVENT_ORGANIZERS, (org, i) => slugify(org.name || String(i)));

  const filtered = mc.items
    .filter(
      (org) =>
        !MOVED_OUT_ORGANIZER_NAMES.has((org.name || "").toLowerCase()) &&
        (query.trim() === "" ||
          org.name.toLowerCase().includes(query.trim().toLowerCase()))
    )
    .sort((a, b) => {
      const aPinned = PIN_TO_FRONT.has((a.name || "").toLowerCase()) ? 0 : 1;
      const bPinned = PIN_TO_FRONT.has((b.name || "").toLowerCase()) ? 0 : 1;
      if (aPinned !== bPinned) return aPinned - bPinned;
      return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
    });

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        placeholder="Search event organizers…"
        query={query}
        setQuery={setQuery}
        hideLocation
        backgroundImage="https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-25/8de293f9fcb1.png"
      />
      <NewsTicker />

      <section
        className="bg-gray-50/40 py-8"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          {/* LEFT: organizers */}
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-1">
              Indian, Pakistani & South Asian Marketing & Production Services in Dallas–Fort Worth
            </h2>
            <p className="text-[13px] text-gray-500 mb-5">
              Discover Indian, Pakistani, South Asian, and Desi marketing and production professionals across the Dallas–Fort Worth area with DallasDesiScoop. Find local businesses offering digital marketing, social media marketing, branding, advertising, content creation, video production, photography, graphic design, website services, public relations, event promotion, and media production for businesses, events, organizations, and community projects.
            </p>
            <AdminAddBar mc={mc} fields={ORGANIZER_FIELDS} label="Add Organizer" />
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                {filtered.map((org, i) => (
                  <AdminItem key={org._key} mc={mc} item={org} fields={ORGANIZER_FIELDS} title="Organizer">
                    <OrganizerCard organizer={org as any} index={i} />
                  </AdminItem>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-[13px] text-gray-500">
                No event organizers match your filters.
              </div>
            )}
            <p className="text-[13px] text-gray-500 mt-6">
              Looking for Indian marketing companies in Dallas, South Asian marketing services, Desi digital marketing agencies, video production companies, social media professionals, event promotion services, or creative production services near you? Explore marketing and production professionals serving Dallas, Frisco, Plano, Irving, McKinney, Allen, The Colony, Prosper, Lewisville, Carrollton, Coppell, Richardson, Flower Mound, Little Elm, and surrounding DFW communities. DallasDesiScoop helps you discover and connect with local professionals for business marketing, brand promotion, social media, advertising, events, and creative projects across North Texas.
            </p>
          </div>

          {/* Instagram widget removed — only on home page */}
        </div>
      </section>
    </div>
  );
}
